import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { ApplePaySettings } from '@/types/applePay';
import { isSafari } from '@/utils/isSafari';

interface ApplePayContextValue {
  settings: ApplePaySettings | null;
  loading: boolean;
  error: string | null;
  isAvailable: boolean;
}

const ApplePayContext = createContext<ApplePayContextValue | undefined>(undefined);

interface ApplePayProviderProps {
  children: ReactNode;
}

export const ApplePayProvider: React.FC<ApplePayProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<ApplePaySettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAvailable, setIsAvailable] = useState(false);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        // Check if Apple Pay is available in the browser
        // Only check in Safari browsers as Apple Pay is Safari-only
        let applePayAvailable = false;

        // FIRST: Check if we're in Safari
        const isInSafari = isSafari();

        if (!isInSafari) {
          console.log('ApplePayContext: Not running in Safari, Apple Pay unavailable');
          setIsAvailable(false);
        } else {
          // SECOND: Check if ApplePaySession exists and is properly defined
          const hasApplePaySession = typeof window.ApplePaySession !== 'undefined';

          if (!hasApplePaySession) {
            console.log('ApplePayContext: ApplePaySession not available in window');
            setIsAvailable(false);
          } else {
            // THIRD: Check if canMakePayments method exists and can be called
            try {
              const hasCanMakePayments = typeof window.ApplePaySession.canMakePayments === 'function';

              if (!hasCanMakePayments) {
                console.warn('ApplePayContext: ApplePaySession.canMakePayments is not a function');
                applePayAvailable = false;
              } else {
                // FOURTH: Actually call canMakePayments
                // This is the definitive check - returns true only if:
                // 1. User has Apple Pay capable device
                // 2. User has cards set up in Wallet
                // 3. Browser truly supports Apple Pay
                applePayAvailable = window.ApplePaySession.canMakePayments();

                console.log('ApplePayContext: canMakePayments returned:', applePayAvailable);

                // FIFTH: Additional validation - check if we can create a session
                // This catches cases where canMakePayments might return true but sessions fail
                if (applePayAvailable) {
                  try {
                    // Try to check if ApplePaySession constructor is valid
                    const isConstructorValid = typeof window.ApplePaySession === 'function';
                    if (!isConstructorValid) {
                      console.warn('ApplePayContext: ApplePaySession is not a constructor');
                      applePayAvailable = false;
                    }
                  } catch (constructorError) {
                    console.warn('ApplePayContext: ApplePaySession constructor check failed:', constructorError);
                    applePayAvailable = false;
                  }
                }
              }
            } catch (error) {
              console.warn('ApplePayContext: canMakePayments check failed:', error);
              applePayAvailable = false;
            }
          }
        }

        setIsAvailable(applePayAvailable);

        console.log('ApplePayContext: Browser support check:', {
          isSafari: isInSafari,
          hasApplePaySession: typeof window.ApplePaySession !== 'undefined',
          applePayAvailable
        });

        // Fetch configuration from API endpoint
        console.log('ApplePayContext: Fetching config from /Checkout/GetApplePayConfig');
        const response = await fetch('/Checkout/GetApplePayConfig');

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const apiConfig = await response.json();
        console.log('ApplePayContext: API response:', apiConfig);

        // Create final config
        const finalConfig: ApplePaySettings = {
          enabled: apiConfig.enabled && applePayAvailable, // Only enabled if both backend and browser support
          merchantId: apiConfig.merchantId || '',
          merchantName: apiConfig.merchantName || 'Eason',
          environment: apiConfig.environment || 'PRODUCTION',
          countryCode: apiConfig.countryCode || 'IE',
          currencyCode: apiConfig.currencyCode || 'EUR',
          supportedNetworks: apiConfig.supportedNetworks || ['visa', 'masterCard', 'amex', 'discover'],
          merchantCapabilities: apiConfig.merchantCapabilities || ['supports3DS'],
          validCountries: apiConfig.validCountries || [],
          isCouponEntryEnabled: apiConfig.isCouponEntryEnabled || false
        };

        console.log('ApplePayContext: Final config loaded:', finalConfig);
        setSettings(finalConfig);
        setError(null);

      } catch (err) {
        console.error('ApplePayContext: Error loading configuration:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to load Apple Pay configuration';
        setError(errorMessage);

        // Set fallback config with enabled: false
        setSettings({
          enabled: false,
          merchantId: '',
          merchantName: 'Eason',
          environment: 'PRODUCTION',
          countryCode: 'IE',
          currencyCode: 'EUR',
          supportedNetworks: ['visa', 'masterCard', 'amex', 'discover'],
          merchantCapabilities: ['supports3DS'],
          validCountries: [],
          isCouponEntryEnabled: false
        });
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, []); // Only run once on mount

  const value: ApplePayContextValue = {
    settings,
    loading,
    error,
    isAvailable
  };

  return (
    <ApplePayContext.Provider value={value}>
      {children}
    </ApplePayContext.Provider>
  );
};

export const useApplePaySettings = (): ApplePayContextValue => {
  const context = useContext(ApplePayContext);
  if (context === undefined) {
    throw new Error('useApplePaySettings must be used within an ApplePayProvider');
  }
  return context;
};